// src/components/RedeemCard.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  Image,
  Stack,
  VStack,
  HStack,
  useToast,
} from "@chakra-ui/react";

import {
  FiGlobe,
  FiMapPin,
  FiPhone,
  FiCopy,
  FiShare2,
  FiDownload,
} from "react-icons/fi";

import confetti from "canvas-confetti";
import { Howl } from "howler";
import { motion } from "framer-motion";

// 🎉 Confetti
const fireConfetti = () => {
  confetti({
    particleCount: 180,
    spread: 75,
    origin: { y: 0.6 },
  });
};

// 🔊 Sound (FIXED PATH)
const playSound = () => {
  const s = new Howl({
    src: ["/sounds/correctsuccess.mp3"], // FIXED
    volume: 0.7,
  });
  s.play();
};

const RedeemCard = ({ business, slot, flyer, onTrack }) => {
  const [redeemed, setRedeemed] = useState(false);
  const [used, setUsed] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [expired, setExpired] = useState(false);

  const toast = useToast();

  const offerCode = slot?.offer_code || business?.offer_code || "SHOW IN STORE";
  const offerTitle = slot?.offer_title || business?.offer_title || "🔥 Special Local Offer";
  const offerText = slot?.offer_text || business?.offer_description || "";
  const expiryDate = slot?.expiry || business?.expiry || null;
  const postcode = flyer?.postcode_sector || slot?.postcode || business?.postcode || "";

  // ⭐ Scratch‑off canvas
  const canvasRef = useRef(null);
  const [scratchReady, setScratchReady] = useState(false);
  const [scratchCleared, setScratchCleared] = useState(false);

  // ⭐ Capture PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // ⭐ Expiry countdown timer
  useEffect(() => {
    if (!expiryDate) return;

    const expiry = new Date(expiryDate).getTime();

    const interval = setInterval(() => {
      const now = Date.now();
      const diff = expiry - now;

      if (diff <= 0) {
        setExpired(true);
        setTimeLeft("Expired");
        clearInterval(interval);
        return;
      }

      const hours = Math.floor(diff / 1000 / 60 / 60);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryDate]);

  // ⭐ Redeem handler
  const handleRedeem = () => {
    if (expired) return;

    setRedeemed(true);
    setUsed(true);
    fireConfetti();
    playSound();
    onTrack?.("redeem_coupon");

    toast({
      title: "🎉 Coupon Redeemed!",
      description: "Scratch to reveal your code",
      status: "success",
      duration: 1500,
    });

    setTimeout(() => setScratchReady(true), 300);
  };

  // ⭐ Scratch‑off logic (TIGHTENED + MOBILE FIX)
  useEffect(() => {
    if (!scratchReady || scratchCleared) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = 300;
    canvas.height = 90; // REDUCED HEIGHT

    ctx.fillStyle = "#cccccc";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.globalCompositeOperation = "destination-out";

    const scratch = (e) => {
      e.preventDefault(); // STOP SCREEN MOVING ON MOBILE

      const rect = canvas.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

      ctx.beginPath();
      ctx.arc(x, y, 14, 0, Math.PI * 2); // SMALLER SCRATCH RADIUS
      ctx.fill();

      const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      let cleared = 0;
      for (let i = 3; i < pixels.length; i += 4) {
        if (pixels[i] === 0) cleared++;
      }
      if (cleared / (pixels.length / 4) > 0.6) {
        setScratchCleared(true);
        onTrack?.("scratch_reveal");
      }
    };

    canvas.addEventListener("mousemove", scratch, { passive: false });
    canvas.addEventListener("touchmove", scratch, { passive: false });

    return () => {
      canvas.removeEventListener("mousemove", scratch);
      canvas.removeEventListener("touchmove", scratch);
    };
  }, [scratchReady, scratchCleared]);

  return (
    <Box
      as={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      bg="white"
      borderRadius="2xl"
      boxShadow="2xl"
      maxW="480px"
      w="100%"
      p={6}
    >
      {/* USED BADGE */}
      {used && (
        <Box
          as={motion.div}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
          bg="green.500"
          color="white"
          textAlign="center"
          py={1}
          px={3}
          borderRadius="md"
          fontWeight="bold"
          mb={3}
        >
          ✅ Offer Redeemed
        </Box>
      )}

      {/* Header */}
      <HStack justify="space-between" mb={4}>
        <HStack spacing={3}>
          {business?.logo_url && (
            <Image
              src={business.logo_url}
              alt={business.name}
              boxSize="48px"
              borderRadius="full"
              objectFit="cover"
            />
          )}
          <VStack align="start" spacing={0}>
            <Heading size="md" color="red.600">
              {business?.name || "Local Business"}
            </Heading>
            <Text fontSize="xs" color="gray.500">
              📍 {postcode} • {flyer?.season || ""}
            </Text>
          </VStack>
        </HStack>

        <Image src="/logo512.png" alt="postcode.coupons" boxSize="40px" />
      </HStack>

      {/* Offer */}
      <Box
        bg="red.500"
        color="white"
        borderRadius="xl"
        p={5}
        mb={3}
        boxShadow="md"
      >
        <Heading size="lg" mb={2}>
          🎁 {offerTitle}
        </Heading>

        {offerText && (
          <Text fontSize="sm" mb={3}>
            {offerText}
          </Text>
        )}

        <Text fontSize="xs" opacity={0.9}>
          Show this screen in store to claim this offer. Subject to availability.
        </Text>
      </Box>

      {/* Expiry Timer */}
      {timeLeft && (
        <Text
          fontSize="sm"
          fontWeight="bold"
          color={expired ? "red.600" : "red.500"}
          textAlign="center"
          mb={3}
        >
          ⏳ Expires in: {timeLeft}
        </Text>
      )}

      {/* Redeem Button */}
      {!redeemed && !expired && !used && (
        <Button
          colorScheme="red"
          size="lg"
          w="100%"
          onClick={handleRedeem}
        >
          🎉 Redeem Coupon
        </Button>
      )}

      {/* Scratch‑off Reveal */}
      {redeemed && (
        <Box textAlign="center" mt={4}>
          {!scratchCleared && (
            <>
              <Text fontSize="md" mb={2} color="gray.600">
                🪄 Scratch to reveal your code
              </Text>
              <canvas
                ref={canvasRef}
                style={{
                  width: "100%",
                  height: "90px",
                  borderRadius: "12px",
                  background: "#ffffff",
                  touchAction: "none", // MOBILE FIX
                }}
              />
            </>
          )}

          {scratchCleared && (
            <Box
              as={motion.div}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              mt={4}
              p={4}
              bg="red.100"
              borderRadius="lg"
              textAlign="center"
              boxShadow="md"
            >
              <Heading size="md" color="red.600" mb={2}>
                🔐 Your Coupon Code
              </Heading>

              <Text
                fontSize="2xl"
                fontWeight="bold"
                color="red.700"
                mb={3}
                userSelect="all"
              >
                {offerCode}
              </Text>

              <Button
                size="md"
                colorScheme="red"
                variant="outline"
                leftIcon={<FiCopy />}
                onClick={() => {
                  navigator.clipboard.writeText(offerCode);
                  onTrack?.("copy_code");
                }}
              >
                Copy Code
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Business Actions — ALWAYS SHOW */}
      <Stack
        spacing={3}
        mt={6}
        opacity={used ? 0.3 : 1}
        pointerEvents={used ? "none" : "auto"}
      >
        {/* Website */}
        <Button
          colorScheme="red"
          size="lg"
          leftIcon={<FiGlobe />}
          isDisabled={!business?.website_url}
          onClick={() => {
            if (!business?.website_url) return;
            onTrack?.("website");
            window.location.href = business.website_url;
          }}
        >
          {business?.website_url ? "Visit Website" : "Website Not Provided"}
        </Button>

        {/* Directions */}
        <Button
          variant="outline"
          colorScheme="red"
          size="lg"
          leftIcon={<FiMapPin />}
          isDisabled={!business?.address}
          onClick={() => {
            if (!business?.address) return;
            onTrack?.("directions");
            const q = encodeURIComponent(business.address);
            window.open(
              `https://www.google.com/maps/search/?api=1&query=${q}`,
              "_blank"
            );
          }}
        >
          {business?.address ? "Get Directions" : "Address Not Provided"}
        </Button>

        {/* Call */}
        <Button
          variant="ghost"
          colorScheme="red"
          size="lg"
          leftIcon={<FiPhone />}
          isDisabled={!business?.phone}
          onClick={() => {
            if (!business?.phone) return;
            onTrack?.("call");
            window.location.href = `tel:${business.phone}`;
          }}
        >
          {business?.phone ? "Call Business" : "Phone Not Provided"}
        </Button>

        {/* Share Offer */}
        <Button
          variant="outline"
          colorScheme="red"
          size="lg"
          leftIcon={<FiShare2 />}
          onClick={() => {
            onTrack?.("share_offer");

            const shareData = {
              title: business?.name || "Local Offer",
              text: `${offerTitle} — redeem this local offer 🎉`,
              url: window.location.href,
            };

            if (navigator.share) {
              navigator.share(shareData);
            } else {
              navigator.clipboard.writeText(window.location.href);
              toast({
                title: "🔗 Link copied",
                description: "Offer link copied to clipboard",
                status: "success",
                duration: 1500,
              });
            }
          }}
        >
          Share Offer
        </Button>

        {/* Save to Home Screen */}
        {deferredPrompt && (
          <Button
            colorScheme="red"
            size="lg"
            leftIcon={<FiDownload />}
            onClick={async () => {
              onTrack?.("save_to_home_screen");
              deferredPrompt.prompt();
              const choice = await deferredPrompt.userChoice;
              if (choice.outcome === "accepted") {
                toast({
                  title: "📲 Added to Home Screen",
                  description: "You can now access this offer instantly",
                  status: "success",
                  duration: 1500,
                });
              }
            }}
          >
            Save to Home Screen
          </Button>
        )}
      </Stack>

      {/* Footer */}
      <Box mt={3}>
        {business?.address && (
          <Text fontSize="xs" color="gray.600" mb={1}>
            🏪 {business.address}
          </Text>
        )}
        <Text fontSize="xs" color="gray.400">
          Powered by postcode.coupons • Offers may be withdrawn at any time.
        </Text>
      </Box>
    </Box>
  );
};

export default RedeemCard;
